import type { Placement } from '@floating-ui/react';
import { useField } from 'formik';
import type { ReactNode } from 'react';
import { z } from 'zod';
import type {
  DirectionFilter,
  TradingMarketFilter,
} from 'src/apis/network-points/types';
import { useFormMode } from 'src/components/form/form-mode-context';
import { NetworkPointPopover } from 'src/components/form/select/network-point-select/network-point-popover';
import { useNetworkPointSelectSearch } from 'src/components/form/select/network-point-select/use-network-point-select-search';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { cn } from 'src/utils/cn';

export const networkPointOptionSchema = z.object({
  hint: z.string(),
  ...formOptionSchema.shape,
});

export type NetworkPointOption = z.input<typeof networkPointOptionSchema>;

type NetworkPointMultiSelectProps = {
  name: string;
  label: string;
  hideLabel?: boolean;
  stacked?: boolean;
  inline?: boolean;
  initialSearch?: string;
  disabled?: boolean;
  disabledMessage?: ReactNode;
  placeholder?: string;
  initialOpen?: boolean;
  hint?: string;
  onChange?: (values: string[]) => void;
  leftAddon?: ReactNode;
  placement?: Placement;
  markAsRequired?: boolean;
  tradingMarket?: TradingMarketFilter;
  direction?: DirectionFilter | DirectionFilter[];
};

export function NetworkPointMultiSelect(props: NetworkPointMultiSelectProps) {
  const [field] = useField<NetworkPointOption[]>(props.name);

  const { isStacked } = useDefaultStacked();

  const {
    name,
    label,
    placeholder,
    stacked = isStacked,
    inline,
    hideLabel = false,
    disabled,
    disabledMessage,
    initialOpen,
    hint,
    initialSearch,
    onChange,
    leftAddon,
    placement,
    markAsRequired,
    tradingMarket,
    direction,
  } = props;

  const formMode = useFormMode();

  const {
    networkPointsRoot,
    internalParams,
    setInternalParams,
    params,
    networkPoints,
    loadMoreNetworkPoints,
    searchValue,
    setSearchValue,
    onFocus,
    searchValueForCurrentOptions,
  } = useNetworkPointSelectSearch({
    initialSearch,
    tradingMarket,
    direction,
  });

  const pending = networkPoints.pending;
  const options = networkPoints.response?.data._embedded.items.map(
    (item) =>
      ({
        label: item.name,
        value: item.id,
        hint: item.bundle
          ? `Exit: ${item.exit.operatorShortName}, Entry: ${item.entry.operatorShortName}`
          : `${item.direction === 'EXIT' ? 'Exit' : 'Entry'}: ${item.operatorShortName}`,
      }) satisfies NetworkPointOption
  );

  return (
    <SearchableMultiSelect
      name={name}
      label={label}
      placeholder={placeholder}
      stacked={stacked}
      inline={inline}
      disabled={disabled}
      disabledMessage={disabledMessage}
      setSearchValue={setSearchValue}
      options={options}
      searchValue={searchValue}
      searchValueForCurrentOptions={searchValueForCurrentOptions}
      pending={pending}
      hint={hint}
      hideLabel={hideLabel}
      leftAddon={leftAddon}
      markAsRequired={markAsRequired}
      popoverProps={(props) => ({
        ...props,
        className: cn(props.className, 'max-h-[48rem] min-w-[90rem]'),
        'data-testid': networkPoints.response
          ? 'nwp-search-results-ready'
          : undefined,
      })}
      renderOptions={() => (
        <NetworkPointPopover
          internalParams={internalParams}
          setInternalParams={setInternalParams}
          params={params}
          networkPointsRoot={networkPointsRoot}
          networkPoints={networkPoints}
          loadMoreNetworkPoints={loadMoreNetworkPoints}
          formMode={formMode}
          pending={pending}
          items={networkPoints.response?.data._embedded.items}
          name={name}
          searchValueForCurrentOptions={searchValueForCurrentOptions}
          type="multi"
          selectedIds={field.value.map((item) => item.value)}
        />
      )}
      onChange={onChange}
      initialOpen={initialOpen}
      placement={placement}
      onFocus={onFocus}
    />
  );
}
